import styles from './MonthNav.module.scss'
import cx from 'classnames'
import ChevronCircleLeft from '/public/assets/svg/chevron_circle_left.svg'
import ChevronCircleRight from '/public/assets/svg/chevron_circle_right.svg'
import { useEventsStore } from 'store/useEventsStore'
import dayjs from 'dayjs'
import Slider from 'react-slick'
import { CAROUSEL_ANIMATION, DATE_FORMAT } from '~constants'
import { useRef } from 'react'
import { useVisibleSlidesCalculation } from '~utils/hooks/useVisibleSlidesCalculation'
import { useWindowSize } from '~utils/hooks'
import ScrollSlider from 'components/elements/CarouselV2/ScrollSlider'

const MONTH_NAV_CARD_WIDTHS = {
  MOBILE: 136,
  TABLET: 150,
  DESKTOP: 185
}

const MONTH_NAV_CONFIG = {
  CARD_MARGIN: 8,
  CARD_MARGIN_MD_UP: 16,
  CONTAINER_PADDING_MULTIPLIER: 1.2,
  MIN_VISIBLE_CARDS: 2,
  DEFAULT_VISIBLE_CARDS: 5,
  DEFAULT_SLIDES_TO_SCROLL: 3,
  RESIZE_DEBOUNCE_MS: 100,
  SCROLL_THRESHOLD: 3,
  ANIMATION_DELAY_MS: 100
}

export const MonthNav: React.FC<{ isLoading?: boolean }> = ({ isLoading }) => {
  // Extract necessary state and actions from the global store
  const {
    setSelectedMonthForSingleArtist,
    selectedMonthForSingleArtist,
    activeMonthListForSingleArtist: activeMonthList
  } = useEventsStore()
  const containerRef = useRef<HTMLDivElement>(null)

  const { visibleSlides, slidesToScroll } = useVisibleSlidesCalculation(containerRef, {
    cardWidths: MONTH_NAV_CARD_WIDTHS,
    config: {
      ...MONTH_NAV_CONFIG
    }
  })
  const scrollSliderBreakpointCheck = useWindowSize('sm', '<')
  const shouldUseScrollSlider = scrollSliderBreakpointCheck.isBreakpoint

  // Handle month selection
  const handleMonthSelection = (monthKey: string) => {
    if (isLoading) return
    setSelectedMonthForSingleArtist(dayjs(monthKey, DATE_FORMAT.MONTH_YEAR_SHORT).format('YYYY-MM-DD'))
  }

  const ArrowButton: React.FC<{
    direction: 'next' | 'prev'
    className?: string
    style?: Partial<CSSStyleDeclaration>
    onClick?: () => void
  }> = ({ direction, className, style, onClick }) => {
    // Determine the correct icon and aria-label based on direction
    const Icon = direction === 'next' ? ChevronCircleRight : ChevronCircleLeft
    const ariaLabel = `${direction === 'next' ? 'Next' : 'Previous'} Month. Currently showing ${
      selectedMonthForSingleArtist?.label
    }`
    return (
      <div className={cx('month-nav-arrow', className)} style={{ ...style } as React.CSSProperties}>
        <button
          className={styles.arrow}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            onClick?.()
          }}
          type='button'
          aria-label={ariaLabel}
          data-testid={`${direction}-month-btn`}
        >
          <Icon className='arrowSVG' style={{ display: 'block' }} />
        </button>
      </div>
    )
  }

  const settings = {
    dots: false,
    infinite: false,
    speed: CAROUSEL_ANIMATION.SPEED,
    nextArrow: <ArrowButton direction='next' />,
    prevArrow: <ArrowButton direction='prev' />,
    slidesToShow: visibleSlides,
    slidesToScroll: slidesToScroll
  }

  const MonthNavCard: React.FC<{
    month: { month: string; event_count: number }
  }> = ({ month }) => {
    return (
      <div
        onClick={() => handleMonthSelection(month?.month)}
        className={cx(
          styles['month-nav-item'],
          selectedMonthForSingleArtist?.shortLabel ===
            dayjs(month?.month, DATE_FORMAT.MONTH_YEAR_SHORT).format('MMM-YYYY')
            ? styles['active']
            : ''
        )}
      >
        <div className={styles['month-nav-item-wrapper']}>
          <div className={styles['month-nav-text']}>
            {dayjs(month.month, DATE_FORMAT.MONTH_YEAR_SHORT).format('MMM YYYY')}
          </div>
          <div className={styles['month-nav-show-text']}>{month?.event_count} Shows</div>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={styles.monthNav}>
      <div className={cx('month-nav-slider', styles.centerNavSlider)}>
        {shouldUseScrollSlider ? (
          <ScrollSlider className={styles.scrollSliderWrapper}>
            {activeMonthList.map((month, idx) => {
              return <MonthNavCard key={idx} month={month} />
            })}
          </ScrollSlider>
        ) : (
          <Slider {...settings}>
            {activeMonthList.map((month, idx) => {
              return <MonthNavCard key={idx} month={month} />
            })}
          </Slider>
        )}
      </div>
    </div>
  )
}
